import React from 'react';
import { useSelector } from 'react-redux';
import { CalendarOutlined, UserSwitchOutlined } from '@ant-design/icons';

import { AssignedTechId, EventId } from 'components/DataFieldManager';
import { selectSelectedSubModule } from 'components/Modules/selectors';
import { useTheme } from 'hooks';
import {
	getUserAssignmentCompatibilityMessage,
	MAX_BULK_EVENT_ORDERS,
} from 'hooks/Orders/useOrderAssignments.utils';
import {
	canShowBulkOrderActions,
	getSelectedOrders,
	getUserAssignmentSelectionKey,
} from './BulkOrderActionsBar.utils';

import styles from './BulkOrderActionsBar.module.css';

const Action = ({ icon: Icon, children, title }) => (
	<div className={styles.action} title={title}>
		<Icon className={styles.actionIcon} aria-hidden='true' />
		{children}
	</div>
);

const BulkOrderActionsBar = ({
	datasource,
	datasourceFields,
	selectedItemIds,
	selectionKey,
	tableContext,
}) => {
	const selectedSubModule = useSelector(selectSelectedSubModule);
	const { theme, isDarkTheme } = useTheme();
	const selectedOrders = React.useMemo(
		() => getSelectedOrders({ datasource, selectedItemIds, selectionKey }),
		[datasource, selectedItemIds, selectionKey],
	);

	if (
		!canShowBulkOrderActions({
			selectedSubModule,
			tableContext,
			selectedOrders,
		})
	)
		return null;

	const assignedTechField = datasourceFields.find(
		field => field.id === 'assignedTechId',
	);
	const eventField = datasourceFields.find(field => field.id === 'eventId');
	const selection = { items: selectedItemIds, itemKey: selectionKey };
	const technicianDisabledReason =
		getUserAssignmentCompatibilityMessage(selectedOrders);
	const eventDisabledReason =
		selectedOrders.length > MAX_BULK_EVENT_ORDERS
			? `Puedes cambiar el evento de hasta ${MAX_BULK_EVENT_ORDERS} órdenes a la vez`
			: undefined;

	return (
		<div
			className={styles.positioner}
			role='toolbar'
			aria-label='Acciones para órdenes seleccionadas'
		>
			<div
				className={styles.bar}
				style={{
					backgroundColor: isDarkTheme
						? theme._secondaryBackgroundColor
						: theme._primaryBackgroundColor,
					borderColor: theme._borderColor,
					color: theme._primaryColor,
				}}
			>
				<strong className={styles.count}>
					{selectedOrders.length} órdenes seleccionadas
				</strong>
				<div className={styles.divider} aria-hidden='true' />
				<div className={styles.actions}>
					{assignedTechField && (
						<Action icon={UserSwitchOutlined} title={technicianDisabledReason}>
							<AssignedTechId
								key={getUserAssignmentSelectionKey(selectedOrders)}
								field={assignedTechField}
								rowData={selectedOrders}
								selected={selection}
								style='dropdown'
								placeholder='Cambiar técnico'
							/>
						</Action>
					)}
					{eventField && (
						<Action icon={CalendarOutlined} title={eventDisabledReason}>
							<EventId
								field={eventField}
								rowData={selectedOrders}
								style='dropdown'
								placeholder='Cambiar evento'
							/>
						</Action>
					)}
				</div>
			</div>
		</div>
	);
};

export default BulkOrderActionsBar;
